import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { ROUTES } from 'shared/constants/routes';
import { EASE_OUT } from 'shared/components/motion/tokens';
import { HeroProductPreview } from 'modules/jogador/home/components/HeroProductPreview';
import { ArenaPanelPreview } from 'modules/jogador/auth/components/ArenaPanelPreview';
import { BrandLogo } from 'shared/components/brand/BrandLogo';

export type AuthAudience = 'login' | 'jogador' | 'arena';

// O painel da direita mostra o produto para quem está do outro lado: o painel de gestão
// para o dono de arena; a reserva com Pix dividido para o jogador.
const PITCH: Record<AuthAudience, { title: string; subtitle: string }> = {
  login: {
    title: 'Quadra reservada, galera confirmada e o Pix dividido.',
    subtitle: 'Tudo o que acontece na sua arena ou no seu jogo, num só lugar.',
  },
  jogador: {
    title: 'Reserve em segundos. Divida o Pix sem cobrar ninguém.',
    subtitle: 'Arenas de Fortaleza com horários em tempo real, rachas e aulas no mesmo app.',
  },
  arena: {
    title: 'Agenda, cobrança e relatórios da sua arena no automático.',
    subtitle: 'Menos WhatsApp e planilha. Mais quadra ocupada e pagamento em dia.',
  },
};

const STATS = [
  { value: '40+', label: 'arenas parceiras' },
  { value: '12 mil+', label: 'reservas feitas' },
  { value: '4,8/5', label: 'avaliação média' },
];

interface AuthLayoutProps {
  audience: AuthAudience;
  children: ReactNode;
}

function Logo() {
  return (
    <Link to={ROUTES.HOME} aria-label="Marcô, ir para o início">
      <BrandLogo />
    </Link>
  );
}

// Tela de acesso em tela cheia, sem o cabeçalho do app: formulário à esquerda e o produto à
// direita (só no desktop). No celular, só o formulário.
export function AuthLayout({ audience, children }: AuthLayoutProps) {
  const pitch = PITCH[audience];

  return (
    <div className="grid min-h-screen bg-surface lg:grid-cols-2">
      <div className="flex flex-col px-6 py-6 sm:px-10 lg:px-16">
        <header className="flex items-center justify-between">
          <Logo />
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-1.5 text-sm font-medium text-ink-secondary transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar ao site
          </Link>
        </header>

        <main className="flex flex-1 items-center justify-center py-10">
          <motion.div
            className="w-full max-w-[420px]"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE_OUT }}
          >
            {children}
          </motion.div>
        </main>

        <footer className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-secondary">
          <span>© 2026 Marcô · Fortaleza-CE</span>
          <span className="flex gap-4">
            <span className="cursor-default hover:text-ink">Termos de uso</span>
            <span className="cursor-default hover:text-ink">Privacidade</span>
          </span>
        </footer>
      </div>

      <aside className="relative hidden flex-col justify-between overflow-hidden bg-ink px-16 py-14 text-surface lg:flex">
        {/* Só uma textura sutil de grade: laranja borrado sobre o azul-marinho vira marrom. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage: 'radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%)',
          }}
        />

        <motion.div
          key={audience}
          className="relative"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE_OUT }}
        >
          <h2 className="max-w-lg font-heading text-3xl font-bold leading-tight xl:text-4xl">{pitch.title}</h2>
          <p className="mt-4 max-w-md text-base text-surface/60">{pitch.subtitle}</p>
        </motion.div>

        <div className="relative flex flex-1 items-center justify-center py-6">
          {audience === 'arena' ? <ArenaPanelPreview key="arena" /> : <HeroProductPreview key="player" />}
        </div>

        <dl className="relative grid grid-cols-3 gap-6 border-t border-white/10 pt-6">
          {STATS.map((stat) => (
            <div key={stat.label}>
              <dt className="text-xs text-surface/50">{stat.label}</dt>
              <dd className="mt-1 font-heading text-2xl font-bold">{stat.value}</dd>
            </div>
          ))}
        </dl>
      </aside>
    </div>
  );
}
